import React, { useMemo } from 'react';
import type { DynamicComponentConfig } from '../components/common/DynamicRenderer/DynamicRendererLoader';
import AppLayout from '../components/common/AppLayout/AppLayout';

// Mocks fora do componente para evitar recriação
const mockFields = [
  // Primeira linha
  [
    { id: 'nome', type: 'input', inputType: 'text', label: 'Nome', required: true },
    { id: 'sobrenome', type: 'input', inputType: 'text', label: 'Sobrenome', required: true },
    { id: 'email', type: 'input', inputType: 'email', label: 'E-mail', required: true },
  ],
  // Segunda linha
  [
    { id: 'telefone', type: 'input', inputType: 'phone', label: 'Telefone', required: false },
    { id: 'cpf', type: 'input', inputType: 'cpf', label: 'CPF', required: true },
    { id: 'rg', type: 'input', inputType: 'text', label: 'RG', required: false },
  ],
  // Terceira linha
  [
    { id: 'dataNascimento', type: 'input', inputType: 'date', label: 'Data de Nascimento', required: true },
    {
      id: 'sexo',
      type: 'select',
      label: 'Sexo',
      required: false,
      options: [
        { label: 'Masculino', value: 'Masculino' },
        { label: 'Feminino', value: 'Feminino' },
        { label: 'Outro', value: 'Outro' },
      ],
    },
    { id: 'endereco', type: 'input', inputType: 'text', label: 'Endereço', required: true },
  ],
  // Quarta linha
  [
    { id: 'numero', type: 'input', inputType: 'text', label: 'Número', required: true },
    { id: 'complemento', type: 'input', inputType: 'text', label: 'Complemento', required: false },
    { id: 'bairro', type: 'input', inputType: 'text', label: 'Bairro', required: true },
  ],
  // Quinta linha
  [
    { id: 'cidade', type: 'input', inputType: 'text', label: 'Cidade', required: true },
    { id: 'estado', type: 'input', inputType: 'text', label: 'Estado', required: true },
    { id: 'cep', type: 'input', inputType: 'cep', label: 'CEP', required: true },
  ],
  // Sexta linha
  [
    { id: 'pais', type: 'input', inputType: 'text', label: 'País', required: true },
    { id: 'profissao', type: 'input', inputType: 'text', label: 'Profissão', required: false },
    { id: 'empresa', type: 'input', inputType: 'text', label: 'Empresa', required: false },
  ],
  // Sétima linha
  [
    { id: 'cargo', type: 'input', inputType: 'text', label: 'Cargo', required: false },
    {
      id: 'escolaridade',
      type: 'select',
      label: 'Escolaridade',
      required: false,
      options: [
        { label: 'Fundamental', value: 'Fundamental' },
        { label: 'Médio', value: 'Médio' },
        { label: 'Superior', value: 'Superior' },
        { label: 'Pós', value: 'Pós' },
      ],
    },
    {
      id: 'estadoCivil',
      type: 'select',
      label: 'Estado Civil',
      required: false,
      options: [
        { label: 'Solteiro', value: 'Solteiro' },
        { label: 'Casado', value: 'Casado' },
        { label: 'Divorciado', value: 'Divorciado' },
        { label: 'Viúvo', value: 'Viúvo' },
      ],
    },
  ],
  // Oitava linha
  [
    { id: 'filhos', type: 'input', inputType: 'number', label: 'Filhos', required: false },
    { id: 'renda', type: 'input', inputType: 'number', label: 'Renda Mensal', required: false },
    { id: 'possuiVeiculo', type: 'checkbox', label: 'Possui Veículo?', required: false, isVisible: true },
  ],
  // Nona linha
  [
    { id: 'possuiImovel', type: 'checkbox', label: 'Possui Imóvel?', required: false, isVisible: true },
    { id: 'corFavorita', type: 'input', inputType: 'text', label: 'Cor Favorita', required: false },
    { id: 'time', type: 'input', inputType: 'text', label: 'Time de Futebol', required: false },
  ],
  // Décima linha
  [
    { id: 'animalEstimacao', type: 'input', inputType: 'text', label: 'Animal de Estimação', required: false },
    { id: 'planoSaude', type: 'checkbox', label: 'Possui Plano de Saúde?', required: false, isVisible: true },
    { id: 'fumante', type: 'checkbox', label: 'Fumante?', required: false, isVisible: true },
  ],
  // Última linha
  [
    { id: 'alergias', type: 'input', inputType: 'text', label: 'Alergias', required: false },
    { id: 'observacoes', type: 'input', inputType: 'text', label: 'Observações', required: false },
    // Adicione um campo vazio para completar 3 colunas
    { id: 'dummy', type: 'input', inputType: 'text', label: '', required: false, isVisible: false },
  ],
];

const mockData = [
  {
    nome: 'João',
    sobrenome: 'Silva',
    email: 'joao@email.com',
    telefone: '11999999999',
    cpf: '123.456.789-00',
    rg: '12.345.678-9',
    dataNascimento: '1990-01-01',
    sexo: 'Masculino',
    endereco: 'Rua A',
    numero: '100',
    complemento: 'Apto 1',
    bairro: 'Centro',
    cidade: 'São Paulo',
    estado: 'SP',
    cep: '01000-000',
    pais: 'Brasil',
    profissao: 'Engenheiro',
    empresa: 'Empresa X',
    cargo: 'Analista',
    escolaridade: 'Superior',
    estadoCivil: 'Solteiro',
    filhos: 0,
    renda: 5000,
    possuiVeiculo: true,
    possuiImovel: false,
    corFavorita: 'Azul',
    time: 'Corinthians',
    animalEstimacao: 'Cachorro',
    planoSaude: true,
    fumante: false,
    alergias: 'Nenhuma',
    observacoes: 'Sem observações',
  },
  {
    nome: 'Maria',
    sobrenome: 'Souza',
    email: 'maria@email.com',
    telefone: '21988888888',
    cpf: '987.654.321-00',
    rg: '98.765.432-1',
    dataNascimento: '1985-05-15',
    sexo: 'Feminino',
    endereco: 'Rua B',
    numero: '200',
    complemento: '',
    bairro: 'Bairro Alto',
    cidade: 'Rio de Janeiro',
    estado: 'RJ',
    cep: '20000-000',
    pais: 'Brasil',
    profissao: 'Médica',
    empresa: 'Hospital Y',
    cargo: 'Médica',
    escolaridade: 'Pós',
    estadoCivil: 'Casado',
    filhos: 2,
    renda: 12000,
    possuiVeiculo: true,
    possuiImovel: true,
    corFavorita: 'Verde',
    time: 'Flamengo',
    animalEstimacao: 'Gato',
    planoSaude: true,
    fumante: false,
    alergias: 'Lactose',
    observacoes: 'Vegetariana',
  },
];

// Menu mockado para a sidebar
const mockMenu = [
  { to: 'usuarios', label: 'Usuários', icon: 'users' },
  { to: 'dashboard', label: 'Dashboard', icon: 'dashboard' },
  { to: 'eventos', label: 'Eventos', icon: 'calendar' },
  { to: 'relatorios', label: 'Relatórios', icon: 'bar_chart' },
  { to: 'configuracoes', label: 'Configurações', icon: 'settings' },
];

const dashboardConfig: DynamicComponentConfig[] = [
  {
    type: 'card',
    content: {
      component: 'info',
      props: {
        title: 'Bem-vindo!',
        body: 'Este é o painel inicial.',
      },
    },
  },
];

// Função utilitária para inicializar formData corretamente

const HomePage: React.FC = () => {
  const dynamicConfig: DynamicComponentConfig[] = useMemo(
    () => [
      {
        type: 'card',
        content: {
          component: 'form',
          props: {
            config: {
              title: 'Cadastro Completo',
              fields: mockFields,
              columns: 3,
              buttons: {
                create: [{ title: 'Salvar', action: 'submit' }],
              },
            },
            collectionName: 'usuarios',
            mode: 'create',
            theme: 'light',
          },
        },
      },
      {
        type: 'card',
        content: {
          component: 'list',
          props: {
            title: 'Usuários Cadastrados',
            data: mockData,
            onDataChange: () => {},
            columnsConfig: mockFields
              .flat()
              .filter(f => ('isVisible' in f ? f.isVisible !== false : true) && f.id && f.label) // <-- ajuste aqui
              .map(f => ({
                key: f.id,
                label: f.label,
                type: f.type === 'select' ? 'string' : f.type,
                essential: true,
              })),
            theme: 'light',
          },
        },
      },
    ],
    []
  );

  // Exemplo de função para seleção de menu
  const handleMenuSelect = (key: string) => {
    // Lógica customizada ao trocar de menu
    console.log('Menu selecionado:', key);
  };

  // Exemplo de renderização customizada de conteúdo
  // const renderContent = (selectedKey: string, menuItem?: typeof mockMenu[0]) => (
  //   <div>
  //     <h2>{menuItem?.label}</h2>
  //     <p>Conteúdo customizado para: {selectedKey}</p>
  //   </div>
  // );

  return (
    <AppLayout
      menu={mockMenu}
      columns={1}
      rows={2}
      dynamicConfigs={{
        dashboard: dashboardConfig,
        usuarios: dynamicConfig,
      }}
      selectedMenu="usuarios"
      onMenuSelect={handleMenuSelect}
      renderContent={undefined}
      userName="Gabriel"
      userAvatarUrl="https://ui-avatars.com/api/?name=Gabriel"
      className="custom-applayout"
      mainClassName="custom-main"
      style={{ background: "#f5f5f5" }}
      mainStyle={{ minHeight: 600 }}
    />
  );
};

export default HomePage;
